import { useState, useMemo, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { PermissionGate } from "@/components/PermissionGate";
import { useRole } from "@/data/RoleContext";
import { useAuth } from "@/contexts/AuthContext";
import { getCurrentRepName } from "@/lib/getCurrentRepName";
import { ConversationThread } from "@/components/ConversationThread";
import { PIPELINE_STAGES, STAGE_COLORS, type PipelineStage } from "@/data/pipelineTypes";
import type { Opportunity } from "@/data/opportunityTypes";
import { getLeadBlockers } from "@/data/unifiedBlockers";
import { getLeadNextSteps } from "@/data/nextStepEngine";
import { RecordHealthPanel } from "@/components/RecordHealthPanel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  ArrowLeft, Phone, Mail, MapPin, User, Calendar, FileText, Briefcase,
  Clock, ChevronRight, Plus, MessageSquare, PhoneCall, CalendarPlus, Edit, Save, X,
  Megaphone, Link2, Lock, Target, Zap,
} from "lucide-react";
import { StageOverrideDialog, canOverrideStage } from "@/components/StageOverrideDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { isGrowthChannelSource, getGrowthChannelLabel } from "@/lib/growthChannel";
import { suggestChannel, getChannelById } from "@/lib/growthChannelMapping";
import { getBookingSuggestions } from "@/lib/sourceIntelligence";
import { format } from "date-fns";
import { formatSafe, formatDateOrDatetime } from "@/lib/dateUtils";
import { LeadService } from "@/services/lead.service";
import { QuoteService } from "@/services/quote.service";
import { AppointmentService } from "@/services/appointment.service";
import { CustomerService } from "@/services/customer.service";
import { ConversationService } from "@/services/conversation.service";
import { errorPersistenceLog, startPersistenceLog, successPersistenceLog } from "@/lib/persistenceLogger";
import { scheduleAppointmentTransactional as runScheduleAppointmentTransaction } from "@/lib/leadSchedulingTransaction";
import { findRepAppointmentOverlap } from "@/lib/appointmentOverlap";
import { isUuid } from "@/lib/uuid";
import { resolveProfileDisplayName } from "@/lib/profileDisplayName";
import { useUserResolver } from "@/hooks/useUserResolver";

type LooseRecord = Record<string, unknown>;
type LeadRecordUpdates = {
  stage?: PipelineStage;
  assignedRep?: string;
  appointmentTime?: string;
  updatedAt?: string;
  stageHistory?: Opportunity["stageHistory"];
  ownershipHistory?: Opportunity["ownershipHistory"];
  activities?: Opportunity["activities"];
  primaryName?: string;
  secondaryName?: string;
  primaryPhone?: string;
  secondaryPhone?: string;
  primaryEmail?: string;
  secondaryEmail?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  projectType?: string;
  leadSource?: string;
  notes?: string;
};

function readString(obj: unknown, key: string): string {
  if (!obj || typeof obj !== "object") return "";
  const value = (obj as LooseRecord)[key];
  return typeof value === "string" ? value.trim() : "";
}

function readNestedLeadId(obj: unknown): string {
  if (!obj || typeof obj !== "object") return "";
  const rawPayload = (obj as LooseRecord).raw_payload;
  return readString(rawPayload, "lead_id");
}

function getLeadProjectLabel(lead: any): string {
  const direct = String(lead.projectType ?? "").trim();
  if (direct && direct !== "Other") return direct;

  const cfg = (lead.projectConfig ?? {}) as Record<string, unknown>;
  const cfgProject = String(
    cfg.projectType ??
      cfg.project_type ??
      cfg.projectCategory ??
      cfg.consultationCategory ??
      cfg.scopeCategory ??
      "",
  ).trim();

  if (cfgProject) return cfgProject;

  return direct || "Other";
}

function safeRepLabel(value: string | null | undefined): string {
  return resolveProfileDisplayName(value, undefined, "Unassigned");
}

function firstRow<T = any>(result: T | T[] | null | undefined): T | null {
  if (Array.isArray(result)) return result[0] ?? null;
  return result ?? null;
}

function normalizeDbLeadToUiLead(row: any) {
  if (!row) return null;

  const primaryName = String(
    row.primary_name ??
      [row.first_name, row.last_name].filter(Boolean).join(" ") ??
      "",
  ).trim();

  return {
    id: String(row.id ?? ""),
    leadId: String(row.id ?? ""),
    quote_number: String(row.quote_number ?? row.source_id ?? row.raw_payload?.lead_id ?? row.id ?? ""),
    displayReference: String(row.source_id ?? row.raw_payload?.lead_id ?? row.id ?? ""),
    stage: row.stage ?? "Lead",
    assignedRep: row.assigned_rep ?? "Unassigned",
    createdAt: String(row.created_at ?? row.received_at ?? ""),
    updatedAt: String(row.updated_at ?? row.created_at ?? ""),
    primaryName,
    secondaryName: row.secondary_name ?? "",
    primaryPhone: row.primary_phone ?? row.phone ?? "",
    secondaryPhone: row.secondary_phone ?? row.alternate_phone ?? "",
    primaryEmail: row.primary_email ?? row.email ?? "",
    secondaryEmail: row.secondary_email ?? "",
    address: row.address ?? row.address_line1 ?? "",
    city: row.city ?? "",
    state: row.state ?? "",
    zip: row.zip ?? row.postal_code ?? "",
    projectType: row.project_type ?? row.normalized_payload?.projectType ?? "Other",
    leadSource: row.lead_source ?? row.provider ?? row.raw_payload?.source_code ?? "",
    notes: row.notes ?? "",
    appointmentTime: row.appointment_time ?? undefined,
    linkedQuoteId: row.linked_quote_id ?? undefined,
    linkedJobId: row.linked_job_id ?? undefined,
    stageHistory: Array.isArray(row.stage_history) ? row.stage_history : [],
    ownershipHistory: Array.isArray(row.ownership_history) ? row.ownership_history : [],
    activities: Array.isArray(row.activities) ? row.activities : [],
    customerId: row.customer_id ?? undefined,
    organizationId: row.organization_id ?? undefined,
    quoteId: row.linked_quote_id ?? undefined,
    totalEstimate: Number(row.total_estimate ?? 0),
    sourceId: row.source_id ?? row.raw_payload?.lead_id ?? undefined,
  };
}

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { leads, setLeads, quotes, isRefreshing } = useAppData();
  const { role } = useRole();
  const { user } = useAuth();
const [showActivity, setShowActivity] = useState(false);
const [activityType, setActivityType] = useState<"call" | "note" | "follow_up">("note");
const [activityText, setActivityText] = useState("");
const [showReassign, setShowReassign] = useState(false);
const [newRep, setNewRep] = useState("");
const [editing, setEditing] = useState(false);
const [showSchedule, setShowSchedule] = useState(false);
const [schedDate, setSchedDate] = useState("");
const [schedTime, setSchedTime] = useState("10:00");
const [scheduleRep, setScheduleRep] = useState("");
const [popoverOpen, setPopoverOpen] = useState(false);
const [isScheduling, setIsScheduling] = useState(false);
  const [pendingStageOverride, setPendingStageOverride] = useState<PipelineStage | null>(null);
  const canOverride = canOverrideStage(role);
  const currentRep = useMemo(() => getCurrentRepName(user) || "Admin", [user]);
  const [resolvedRouteLeadId, setResolvedRouteLeadId] = useState<string | null>(null);
  const [isResolvingRouteLead, setIsResolvingRouteLead] = useState(false);

  const routeId = String(id ?? "").trim();

  const routeQuote = useMemo(() => {
    if (!routeId) return null;

    return quotes.find((q) => {
      const values = [
        q.id,
        readString(q, "leadId"),
        readString(q, "lead_id"),
        readString(q, "quoteId"),
        readString(q, "quote_id"),
        readString(q, "quoteNumber"),
        readString(q, "quote_number"),
        readString(q, "displayReference"),
        readString(q, "display_reference"),
        readString(q, "quoteName"),
        readString(q, "quote_name"),
      ];

      return values.some((value) => String(value ?? "").trim() === routeId);
    }) ?? null;
  }, [quotes, routeId]);

  const lead = useMemo(() => {
    if (!routeId) return undefined;

    const directLead = leads.find((l) =>
      l.id === routeId ||
      readString(l, "leadId") === routeId ||
      readString(l, "sourceId") === routeId ||
      readString(l, "source_id") === routeId ||
      readString(l, "externalLeadId") === routeId ||
      readString(l, "external_lead_id") === routeId ||
      readNestedLeadId(l) === routeId ||
      String(l.linkedQuoteId ?? "").trim() === routeId ||
      readString(l, "quoteId") === routeId ||
      String(l.displayReference ?? "").trim() === routeId,
    );

    if (directLead) return directLead;

    const quoteId = String(routeQuote?.id ?? "").trim();
    const quoteLeadId = String(
      readString(routeQuote, "leadId") ||
        readString(routeQuote, "lead_id") ||
        "",
    ).trim();
    const resolvedLeadId = String(resolvedRouteLeadId ?? "").trim();

    return leads.find((l) =>
      (quoteLeadId && (
        l.id === quoteLeadId ||
        readString(l, "leadId") === quoteLeadId
      )) ||
      (resolvedLeadId && (
        l.id === resolvedLeadId ||
        readString(l, "leadId") === resolvedLeadId
      )) ||
      (quoteId && (
        String(l.linkedQuoteId ?? "").trim() === quoteId ||
        readString(l, "quoteId") === quoteId
      )),
    );
  }, [leads, routeId, routeQuote, resolvedRouteLeadId]);

  const quoteIdForLink: string | undefined =
    (lead?.quoteId as string | null | undefined) ||
    lead?.linkedQuoteId ||
    (routeQuote?.id ? String(routeQuote.id) : undefined);

  const linkedQuote = quoteIdForLink
    ? quotes.find((q) => q.id === quoteIdForLink) ?? routeQuote
    : routeQuote;

  useEffect(() => {
    let cancelled = false;

    async function resolveRouteLead() {
      setResolvedRouteLeadId(null);

      if (!routeId) return;

      const localLead = leads.find((l) =>
        l.id === routeId ||
        readString(l, "leadId") === routeId ||
        String(l.linkedQuoteId ?? "").trim() === routeId ||
        readString(l, "quoteId") === routeId ||
        String(l.displayReference ?? "").trim() === routeId,
      );

      if (localLead) return;

      const quoteLeadId = String(
        readString(routeQuote, "leadId") ||
          readString(routeQuote, "lead_id") ||
          "",
      ).trim();

      if (quoteLeadId) {
        setResolvedRouteLeadId(quoteLeadId);
        return;
      }

      setIsResolvingRouteLead(true);

      try {
        const { data: leadResult } = await LeadService.getById(routeId).catch(() => ({ data: null }));
        if (cancelled) return;

        const leadRow = firstRow(leadResult);
        if (leadRow?.id) {
          const normalizedLead = normalizeDbLeadToUiLead(leadRow);
          if (normalizedLead) {
            setLeads((prev) => {
              const exists = prev.some((l) =>
                l.id === normalizedLead.id ||
                readString(l, "leadId") === normalizedLead.id,
              );

              return exists
                ? prev.map((l) => (l.id === normalizedLead.id ? { ...l, ...normalizedLead } : l))
                : [...prev, normalizedLead];
            });
          }

          setResolvedRouteLeadId(String(leadRow.id));
          return;
        }

        const { data: quoteRow } = await QuoteService.getById(routeId).catch(() => ({ data: null }));
        if (cancelled) return;

        const quote = firstRow(quoteRow);
        const leadIdFromQuote = String(
          readString(quote, "leadId") ||
            readString(quote, "lead_id") ||
            "",
        ).trim();

        if (leadIdFromQuote) {
          setResolvedRouteLeadId(leadIdFromQuote);
        }
      } finally {
        if (!cancelled) setIsResolvingRouteLead(false);
      }
    }

    void resolveRouteLead();

    return () => {
      cancelled = true;
    };
  }, [routeId, leads, routeQuote, setLeads]);

  const reps = useMemo((): string[] => {
    const repSet = new Set(leads.map((l) => l.assignedRep).filter(Boolean));
    return Array.from(repSet).sort();
  }, [leads]);

  const { resolveMany, getName } = useUserResolver();
  const isRepRole = role === "sales";
  const currentRepId = isRepRole ? user?.id ?? null : null;

  useEffect(() => {
    const idsToResolve = [...reps];
    if (currentRepId && isUuid(currentRepId) && !idsToResolve.includes(currentRepId)) {
      idsToResolve.push(currentRepId);
    }
    if (user?.id && !idsToResolve.includes(user.id)) {
      idsToResolve.push(user.id);
    }
    lead?.activities?.forEach((act) => {
      const by = (act as any).createdBy;
      if (by && isUuid(by) && !idsToResolve.includes(by)) idsToResolve.push(by);
    });
    lead?.stageHistory?.forEach((sh) => {
      const by = (sh as any).changedBy;
      if (by && isUuid(by) && !idsToResolve.includes(by)) idsToResolve.push(by);
    });
    if (idsToResolve.length > 0) resolveMany(idsToResolve);
  }, [reps, currentRepId, user?.id, lead?.activities, lead?.stageHistory, resolveMany, isUuid]);

  function getRepDisplayName(repValue: string | null | undefined): string {
    if (!repValue) return "Unassigned";
    return getName(repValue) ?? resolveProfileDisplayName(repValue) ?? repValue;
  }

  const [editForm, setEditForm] = useState(lead ? { ...lead } : null);
  useEffect(() => {
    if (lead) setEditForm({ ...lead });
  }, [lead?.id]);

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [leadConversation, setLeadConversation] = useState<import("@/data/communicationTypes").Conversation | null>(null);

  useEffect(() => {
    if (!lead) {
      setConversationId(null);
      setLeadConversation(null);
      return;
    }

    let cancelled = false;
    const findOrCreate = async () => {
      const existing = await ConversationService.fetchConversationsByLeadIds([lead.id]);
      if (cancelled) return;
      if (existing.length > 0) {
        setConversationId(existing[0].id);
        setLeadConversation(existing[0]);
        return;
      }
      const created = await ConversationService.createConversation({
        contactName: lead.primaryName,
        contactPhone: lead.primaryPhone ?? undefined,
        contactEmail: lead.primaryEmail ?? undefined,
        linkedLeadId: lead.id,
        linkedQuoteId: lead.linkedQuoteId ?? undefined,
        linkedJobId: lead.linkedJobId ?? undefined,
        assignedRep: lead.assignedRep ?? undefined,
        lastMessagePreview: "",
        unreadCount: 0,
      });
      if (!cancelled && created) {
        setConversationId(created.id);
        setLeadConversation(created);
      }
    };
    void findOrCreate();
    return () => { cancelled = true; };
  }, [lead?.id]);

  const leadBlockers = useMemo(
    () => (lead ? getLeadBlockers(lead) : []),
    [lead],
  );

  const leadNextSteps = useMemo(
    () => (lead ? getLeadNextSteps(lead) : []),
    [lead],
  );

  const leadCompleteness = useMemo(
    () =>
      lead
        ? [
            { label: "Contact Name", done: !!lead.primaryName?.trim() },
            { label: "Phone", done: !!lead.primaryPhone?.trim() },
            { label: "Email", done: !!lead.primaryEmail?.trim() },
            { label: "Address", done: !!lead.address?.trim() },
            { label: "Rep Assigned", done: !!lead.assignedRep },
            { label: "Quote Created", done: !!lead.linkedQuoteId },
          ]
        : [],
    [lead],
  );

   const scheduleConflictLead = useMemo(() => {
     if (!lead || !schedDate || !schedTime) return null;
     const targetRep = String(scheduleRep ?? "").trim();
     if (!targetRep || !isUuid(targetRep)) return null;
     return findRepAppointmentOverlap(leads, {
       rep: targetRep,
       candidateStartIso: new Date(`${schedDate}T${schedTime}:00`).toISOString(),
       excludeId: lead.id,
     });
   }, [lead, schedDate, schedTime, scheduleRep, leads]);

  const hasRouteId = !!routeId;

  if (
    hasRouteId &&
    !lead &&
    (isRefreshing || isResolvingRouteLead || leads.length === 0 || quotes.length === 0)
  ) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Loading lead...</p>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Lead not found</p>
        <Button variant="link" onClick={() => navigate("/pipeline")}>Back to Pipeline</Button>
      </div>
    );
  }

  async function persistLeadRecord(
    target: typeof lead,
    updates: LeadRecordUpdates,
  ) {
    const leadId = getLeadIdFromTarget(target);
    const quoteId = String(target.linkedQuoteId ?? "").trim();

    if (quoteId) {
      await QuoteService.update(quoteId, {
        stage: updates.stage,
        status: updates.stage,
        assigned_rep: updates.assignedRep,
        stage_history: updates.stageHistory,
        activities: updates.activities,
        customer_name: updates.primaryName,
        customer_phone: updates.primaryPhone,
        customer_email: updates.primaryEmail,
        customer_address:
          [updates.address, updates.city, [updates.state, updates.zip].filter(Boolean).join(" ")]
            .filter(Boolean)
            .join(", "),
        lead_source: updates.leadSource,
        notes: updates.notes,
      });
    }

    if (leadId) {
      await LeadService.update(leadId, {
        stage: updates.stage,
        assigned_rep: updates.assignedRep,
        appointment_time: updates.appointmentTime,
        stage_history: updates.stageHistory,
        ownership_history: updates.ownershipHistory,
        activities: updates.activities,
        primary_name: updates.primaryName,
        secondary_name: updates.secondaryName,
        primary_phone: updates.primaryPhone,
        secondary_phone: updates.secondaryPhone,
        primary_email: updates.primaryEmail,
        secondary_email: updates.secondaryEmail,
        address: updates.address,
        city: updates.city,
        state: updates.state,
        zip: updates.zip,
        project_type: updates.projectType,
        lead_source: updates.leadSource,
        notes: updates.notes,
      });
    }
  }

  function getLeadIdFromTarget(target: typeof lead): string {
    const maybeLeadId = (target as unknown as { leadId?: string })?.leadId;
    return String(maybeLeadId ?? target.id ?? "").trim();
  }

  async function resolveCanonicalLeadId(target: typeof lead): Promise<string | null> {
    const candidate = getLeadIdFromTarget(target);
    if (!candidate) return null;
    const { data } = await LeadService.getById(candidate);
    if (data?.id) return data.id;
    return null;
  }

  async function ensureCustomerForLead(target: typeof lead): Promise<string | null> {
    if (!target) return null;

    const t = target as unknown as { customerId?: string; customer_id?: string; organizationId?: string };
    const existingCustomerId = String(t.customerId ?? t.customer_id ?? "").trim();

    if (existingCustomerId) return existingCustomerId;

    const canonicalLeadId = await resolveCanonicalLeadId(target);
    if (!canonicalLeadId) return null;

    const { data: leadRow, error: leadError } = await LeadService.getById(canonicalLeadId);
    if (leadError) throw leadError;

    const leadRowMeta = (leadRow ?? {}) as { customer_id?: string | null; organization_id?: string | null };
    const linkedCustomerId = String(leadRowMeta.customer_id ?? "").trim();
    if (linkedCustomerId) {
      setLeads((prev) =>
        prev.map((l) => (l.id === target.id ? { ...l, customerId: linkedCustomerId } : l)),
      );
      return linkedCustomerId;
    }

    const customerPayload = {
      primary_name: target.primaryName || "Unknown Customer",
      name: target.primaryName || "Unknown Customer",
      full_name: target.primaryName || "Unknown Customer",
      email: target.primaryEmail || null,
      primary_email: target.primaryEmail || null,
      phone: target.primaryPhone || null,
      primary_phone: target.primaryPhone || null,
      secondary_name: target.secondaryName || null,
      secondary_phone: target.secondaryPhone || null,
      secondary_email: target.secondaryEmail || null,
      address: target.address || null,
      city: target.city || null,
      state: target.state || null,
      zip: target.zip || null,
      lead_id: canonicalLeadId,
      lead_source: target.leadSource || null,
      notes: target.notes || null,
      organization_id: t.organizationId || leadRowMeta.organization_id || null,
    };

    type UpsertFromLeadResult = { data?: { id?: string; customer_id?: string }; error?: unknown };
    type CustomerServiceWithUpsert = typeof CustomerService & {
      upsertFromLead?: (payload: typeof customerPayload) => Promise<UpsertFromLeadResult>;
    };
    const customerService = CustomerService as CustomerServiceWithUpsert;
    const result: UpsertFromLeadResult | null = customerService.upsertFromLead
      ? await customerService.upsertFromLead(customerPayload)
      : customerService.create
        ? await customerService.create({
            first_name: target.primaryName || "Unknown",
            last_name: null,
            email: target.primaryEmail || null,
            phone: target.primaryPhone || null,
            street_address: target.address || null,
            city: target.city || null,
            state: target.state || null,
            zip: target.zip || null,
            notes: target.notes || null,
          })
        : null;

    if (!result) {
      throw new Error("CustomerService must provide upsertFromLead or create to auto-link lead customers");
    }

    if (result.error) throw result.error;

    const customerId = String(
      result.data?.id ??
        result.data?.customer_id ??
        "",
    ).trim();

    if (!customerId) throw new Error("Customer create/link did not return an id");

    const { error: linkCustomerError } = await LeadService.update(canonicalLeadId, {
      customer_id: customerId,
    });
    if (linkCustomerError) throw linkCustomerError;

    setLeads((prev) =>
      prev.map((l) => (l.id === target.id ? { ...l, customerId } : l)),
    );

    return customerId;
  }

  async function ensureQuoteForLead(target: typeof lead, appointmentId?: string): Promise<string | null> {
    if (!target) return null;
    const t = target as unknown as { organizationId?: string; projectConfig?: Record<string, unknown> };

    const canonicalLeadId = await resolveCanonicalLeadId(target);
    if (!canonicalLeadId) return null;

    const customerId = await ensureCustomerForLead(target);
    if (!customerId) {
      throw new Error("Lead is missing customer linkage and customer auto-link failed");
    }

    const linkedQuoteId = String(target.linkedQuoteId ?? "").trim();

    if (linkedQuoteId) {
      const { error } = await QuoteService.update(linkedQuoteId, {
        appointment_id: appointmentId || null,
        customer_id: customerId,
      });
      if (error) throw error;
      return linkedQuoteId;
    }

    const { data, error } = await QuoteService.upsertByLeadId(canonicalLeadId, {
      quote_name: target.primaryName || "Consultation Quote",
      customer_name: target.primaryName || "",
      customer_email: target.primaryEmail || null,
      customer_phone: target.primaryPhone || null,
      customer_address:
        [target.address, target.city, [target.state, target.zip].filter(Boolean).join(" ")]
          .filter(Boolean)
          .join(", ") || null,
      customer_id: customerId,
      organization_id: t.organizationId || null,
      lead_source: target.leadSource || null,
      status: "Appointment Scheduled",
      notes: target.notes || null,
      total_estimate: 0,
      appointment_id: appointmentId || null,
      project_config: t.projectConfig || {
        consultationCategory: getLeadProjectLabel(target) || "Other",
        projectCategory: getLeadProjectLabel(target) || "Other",
      },
    });

    if (error) throw error;

    const quoteId = String((data as { id?: string } | null)?.id ?? "").trim();
    return quoteId || null;
  }

  async function scheduleAppointmentTransactional(params: {
    targetLead: typeof lead;
    scheduledAtIso: string;
    repName: string;
    appointmentId: string;
    stageHistory: typeof lead.stageHistory;
    activities: typeof lead.activities;
  }): Promise<{ quoteId: string; newlyCreatedQuote: boolean }> {
    const { targetLead, scheduledAtIso, repName, appointmentId, stageHistory, activities } = params;
    return runScheduleAppointmentTransaction(
      {
        ensureCustomer: () => ensureCustomerForLead(targetLead).then(() => undefined),
        resolveCanonicalLeadId: () => resolveCanonicalLeadId(targetLead),
        ensureQuote: (apptId) => ensureQuoteForLead(targetLead, apptId),
        updateLead: (leadId, updates) => LeadService.update(leadId, updates),
        createAppointment: (payload) => AppointmentService.create(payload),
        deleteQuote: async (quoteId) => {
          await QuoteService.delete(quoteId);
        },
      },
      {
        previousLinkedQuoteId: String(targetLead.linkedQuoteId ?? "").trim(),
        previousAppointmentTime: targetLead.appointmentTime,
        scheduledAtIso,
        repName,
        appointmentId,
        contactName: targetLead.primaryName,
        contactPhone: targetLead.primaryPhone,
        contactEmail: targetLead.primaryEmail || undefined,
        activityNotes: `Appointment scheduled from lead detail for ${targetLead.primaryName}`,
        stageHistory,
        activities,
      },
    );
  }

  function updateLead(updates: Partial<typeof lead>) {
    setLeads((prev) => prev.map((l) => (l.id === lead?.id ? { ...l, ...updates, updatedAt: new Date().toISOString() } : l)));
  }

  async function handleStageChange(newStage: PipelineStage, overrideReason?: string) {
    if (newStage === lead!.stage) return;
    const now = new Date().toISOString();
    const rep = user?.id ?? currentRep;
    const overrideTag = overrideReason ? ` [manual override: ${overrideReason}]` : "";
    const nextStageHistory = [
      ...lead!.stageHistory,
      { id: `sh-${Date.now()}`, fromStage: lead!.stage, toStage: newStage, changedAt: now, changedBy: rep },
    ];
    const nextActivities = [
      ...lead!.activities,
      { id: `la-${Date.now()}`, type: "system" as const, description: `Stage changed to "${newStage}"${overrideTag}`, createdAt: now, createdBy: rep },
    ];
    try {
      await persistLeadRecord(lead!, {
        stage: newStage,
        updatedAt: now,
        stageHistory: nextStageHistory,
        activities: nextActivities,
      });
      updateLead({
        stage: newStage,
        stageHistory: nextStageHistory,
        activities: nextActivities,
      });
      toast.success(`Lead moved to ${newStage}`);
    } catch (err) {
      console.error("Failed to persist stage change:", err);
      toast.error("Failed to change lead stage in DB.");
    }
  }

  function requestStageChange(newStage: PipelineStage) {
    if (newStage === lead!.stage) return;
    if (!canOverride) {
      toast.error("Stage advances when work is completed. Use the workflow actions instead.");
      return;
    }
    setPendingStageOverride(newStage);
  }

  async function handleAddActivity() {
    if (!activityText.trim()) return;
    const now = new Date().toISOString();
    const rep = user?.id ?? currentRep;
    const nextActivities = [
      ...lead!.activities,
      { id: `la-${Date.now()}`, type: activityType, description: activityText.trim(), createdAt: now, createdBy: rep },
    ];
    try {
      await persistLeadRecord(lead!, {
        updatedAt: now,
        activities: nextActivities,
      });
      updateLead({
        activities: nextActivities,
      });
      setActivityText("");
      setShowActivity(false);
      toast.success("Activity logged");
    } catch (err) {
      console.error("Failed to persist activity:", err);
      toast.error("Failed to save activity to DB.");
    }
  }

  async function handleReassign() {
    if (!newRep || newRep === lead!.assignedRep) return;
    const now = new Date().toISOString();
    const nextOwnership = [
      ...lead!.ownershipHistory,
      { id: `oh-${Date.now()}`, fromRep: lead!.assignedRep, toRep: newRep, changedAt: now, changedBy: user?.id ?? currentRep },
    ];
    const nextActivities = [
      ...lead!.activities,
      { id: `la-${Date.now()}`, type: "system" as const, description: `Reassigned from ${lead!.assignedRep} to ${newRep}`, createdAt: now, createdBy: user?.id ?? currentRep },
    ];
    try {
      await persistLeadRecord(lead!, {
        assignedRep: newRep,
        updatedAt: now,
        ownershipHistory: nextOwnership,
        activities: nextActivities,
      });
      updateLead({
        assignedRep: newRep,
        ownershipHistory: nextOwnership,
        activities: nextActivities,
      });
      setShowReassign(false);
      toast.success(`Lead reassigned to ${newRep}`);
    } catch (err) {
      console.error("Failed to persist reassignment:", err);
      toast.error("Failed to reassign lead in DB.");
    }
  }

  async function handleSaveEdit() {
    if (!editForm) return;
    const now = new Date().toISOString();
    try {
      await persistLeadRecord(lead!, {
        primaryName: editForm.primaryName,
        secondaryName: editForm.secondaryName,
        primaryPhone: editForm.primaryPhone,
        secondaryPhone: editForm.secondaryPhone,
        primaryEmail: editForm.primaryEmail,
        secondaryEmail: editForm.secondaryEmail,
        address: editForm.address,
        city: editForm.city,
        state: editForm.state,
        zip: editForm.zip,
        projectType: editForm.projectType,
        leadSource: editForm.leadSource,
        notes: editForm.notes,
        updatedAt: now,
      });
      updateLead({
        primaryName: editForm.primaryName,
        secondaryName: editForm.secondaryName,
        primaryPhone: editForm.primaryPhone,
        secondaryPhone: editForm.secondaryPhone,
        primaryEmail: editForm.primaryEmail,
        secondaryEmail: editForm.secondaryEmail,
        address: editForm.address,
        city: editForm.city,
        state: editForm.state,
        zip: editForm.zip,
        projectType: editForm.projectType,
        leadSource: editForm.leadSource,
        notes: editForm.notes,
      });
      setEditing(false);
      toast.success("Lead updated");
    } catch (err) {
      console.error("Failed to persist lead edit:", err);
      toast.error("Failed to update lead in DB.");
    }
  }

  function handleCreateQuote() {
    navigate(`/quotes?newFromLead=${lead!.id}`);
  }

  async function handleScheduleAppointment() {
    if (!schedDate || !lead) return;
    const correlationId = startPersistenceLog("lead_schedule_appointment", {
      entityType: "lead",
      entityId: lead.id,
      route: `/pipeline/${id ?? ""}`,
    });

    try {
      const appointmentTime = new Date(`${schedDate}T${schedTime}:00`).toISOString();
      const targetRep = String(scheduleRep ?? "").trim();
      if (!isUuid(targetRep)) {
        toast.error("Assigned rep is invalid", {
          description: "Lead must be assigned to a valid rep UUID before scheduling.",
        });
        return;
      }
      const conflictingLead = findRepAppointmentOverlap(leads, {
        rep: targetRep,
        candidateStartIso: appointmentTime,
        excludeId: lead.id,
      });
      if (conflictingLead) {
        toast.error("Scheduling conflict", {
          description: `${targetRep || "This rep"} already has an appointment within the 2-hour appointment + 2-hour buffer window.`,
        });
        return;
      }
      const appointmentId = crypto.randomUUID();
      const now = new Date().toISOString();
      const rep = user?.id ?? currentRep;
      const newStage: PipelineStage = "Appointment Scheduled" as PipelineStage;
      const activityDescription = `Appointment scheduled for ${new Date(appointmentTime).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })}`;

      const stageChange = {
        id: `sh-${Date.now()}`,
        fromStage: lead.stage,
        toStage: newStage,
        changedAt: now,
        changedBy: rep,
      };

      const activity = {
        id: `la-${Date.now()}`,
        type: "system" as const,
        description: activityDescription,
        createdAt: now,
        createdBy: rep,
      };

      const nextStageHistory = [...lead.stageHistory, stageChange];
      const nextActivities = [...lead.activities, activity];

      const { quoteId } = await scheduleAppointmentTransactional({
        targetLead: lead,
        scheduledAtIso: appointmentTime,
        repName: rep,
        appointmentId,
        stageHistory: nextStageHistory,
        activities: nextActivities,
      });

      updateLead({
        stage: newStage,
        appointmentTime,
        stageHistory: nextStageHistory,
        activities: nextActivities,
        linkedQuoteId: quoteId,
      });

      setShowSchedule(false);
      setPopoverOpen(false);
      successPersistenceLog("lead_schedule_appointment", correlationId, {
        entityType: "lead",
        entityId: lead.id,
        quoteId,
      });
      toast.success("Appointment scheduled");
    } catch (err) {
      console.error("Failed to persist appointment scheduling:", err);
      errorPersistenceLog("lead_schedule_appointment", correlationId, err, {
        entityType: "lead",
        entityId: lead?.id,
      });
      toast.error("Failed to schedule appointment in DB.");
    }
  }

  const activityIcons = { call: PhoneCall, note: MessageSquare, follow_up: CalendarPlus, email: Mail, system: Clock };
  const currentStageIdx = PIPELINE_STAGES.indexOf(lead.stage);

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Record Health */}
      <RecordHealthPanel title="Lead Health" blockers={leadBlockers} nextSteps={leadNextSteps} completeness={leadCompleteness} compact />

      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/pipeline")}><ArrowLeft className="h-4 w-4 mr-1" /> Pipeline</Button>
        <Separator orientation="vertical" className="h-5" />
        <div className="flex-1">
          <h1 className="text-lg font-bold text-foreground">{lead.primaryName}</h1>
            <p className="text-xs text-muted-foreground">{lead.displayReference} · {getLeadProjectLabel(lead)} · {lead.leadSource}</p>
        </div>
         <div className="flex items-center gap-2">
           <Button size="sm" variant="outline" className="h-7 w-7" onClick={() => { window.open(`tel:${lead.primaryPhone}`, "_self"); toast.info(`Calling ${lead.primaryName}...`); }}>
             <Phone className="h-3.5 w-3.5" />
           </Button>
{!lead.appointmentTime && lead.stage !== "Closed Lost" && lead.stage !== "Closed Won" && (
  <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
    <PopoverTrigger asChild>
      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => {
        const candidateAssigned = String(lead.assignedRep ?? "").trim();
        const defaultRep = isUuid(candidateAssigned)
          ? candidateAssigned
          : (currentRepId && isUuid(currentRepId) ? currentRepId : "");
        setSchedDate(format(new Date(Date.now() + 86400000), "yyyy-MM-dd"));
        setScheduleRep(defaultRep);
        setPopoverOpen(true);
      }}>
        <CalendarPlus className="h-3 w-3" />
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-[280px] max-w-[90vw] p-4">
      <div className="space-y-4">
        <div>
          <Label htmlFor="schedule-rep-select" className="text-xs">Assigned Rep</Label>
          <Select
            value={scheduleRep}
            onValueChange={setScheduleRep}
          >
            <SelectTrigger id="schedule-rep-select" className="h-9 w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {reps.map((r) => <SelectItem key={r} value={r}>{getRepDisplayName(r)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="schedule-date-input" className="text-xs">Date</Label>
          <Input 
            id="schedule-date-input"
            type="date" 
            value={schedDate} 
            onChange={(e) => setSchedDate(e.target.value)} 
            className="h-9"
          />
        </div>
        <div>
          <Label htmlFor="schedule-time-input" className="text-xs">Time</Label>
          <Input 
            id="schedule-time-input"
            type="time" 
            value={schedTime} 
            onChange={(e) => setSchedTime(e.target.value)} 
            className="h-9"
          />
        </div>
        <Button 
          onClick={async () => {
            setIsScheduling(true);
            try {
              await handleScheduleAppointment();
            } finally {
              setIsScheduling(false);
            }
            setPopoverOpen(false);
          }}
          className="w-full"
          disabled={!schedDate || !!scheduleConflictLead || !isUuid(scheduleRep) || isScheduling}
        >
          {isScheduling ? "Scheduling..." : "Schedule"}
        </Button>
      </div>
    </PopoverContent>
  </Popover>
)}
           {!editing ? (
             <PermissionGate permission="leads_edit">
               <Button size="sm" variant="outline" onClick={() => { setEditForm({ ...lead }); setEditing(true); }}>
                 <Edit className="h-3.5 w-3.5 mr-1" /> Edit
               </Button>
             </PermissionGate>
           ) : (
             <>
               <Button size="sm" variant="outline" onClick={() => setEditing(false)}><X className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
               <PermissionGate permission="leads_edit">
                 <Button size="sm" onClick={handleSaveEdit}><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
               </PermissionGate>
             </>
           )}
           {!quoteIdForLink && lead.stage !== "Closed Lost" && (
             <PermissionGate permission="leads_edit">
               <Button size="sm" onClick={handleCreateQuote}><FileText className="h-3.5 w-3.5 mr-1" /> Create Quote</Button>
             </PermissionGate>
           )}
         </div>
      </div>

      {/* Stage Pipeline — display-only by default. Stages advance as work is completed.
          Override is admin/manager only and goes through StageOverrideDialog (proof-based execution). */}
      <Card>
        <CardContent className="p-4">
          <TooltipProvider>
            <div className="flex items-center gap-1 overflow-x-auto">
              {PIPELINE_STAGES.map((stage, idx) => {
                const isActive = stage === lead.stage;
                const isPast = idx < currentStageIdx;
                const chip = (
                  <button
                    type="button"
                    onClick={() => requestStageChange(stage)}
                    disabled={isActive}
                    className={cn(
                      "flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap",
                      isActive ? cn("ring-2 ring-offset-1 ring-primary", STAGE_COLORS[stage]) :
                      isPast ? "bg-muted text-muted-foreground" :
                      "bg-muted/50 text-muted-foreground/60",
                      canOverride && !isActive ? "hover:bg-muted cursor-pointer" : "cursor-default",
                    )}
                  >
                    {!canOverride && !isActive && <Lock className="h-2.5 w-2.5 opacity-50" />}
                    {stage}
                  </button>
                );
                if (isActive) return <span key={stage}>{chip}</span>;
                return (
                  <Tooltip key={stage}>
                    <TooltipTrigger asChild>{chip}</TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-xs">
                      {canOverride
                        ? "Manual override — requires reason and confirmation. Stages normally advance automatically as work is completed."
                        : "Stages advance automatically as work is completed. Use the workflow actions (schedule, send quote, etc.) instead."}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>
          <p className="text-[10px] text-muted-foreground mt-2">
            {canOverride
              ? "Stages advance automatically as work is completed. Manual override available — captured for audit."
              : "Stages advance automatically as work is completed."}
          </p>
        </CardContent>
      </Card>

      <StageOverrideDialog
        open={pendingStageOverride !== null}
        onOpenChange={(open) => !open && setPendingStageOverride(null)}
        fromStage={lead.stage}
        toStage={pendingStageOverride ?? ""}
        entityLabel="Lead"
        entityName={lead.primaryName}
        onConfirm={(reason) => {
          if (pendingStageOverride) handleStageChange(pendingStageOverride, reason);
          setPendingStageOverride(null);
        }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left — Contact & Details */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!editing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Primary Contact</p>
                    <div className="flex items-center gap-2 text-sm"><User className="h-3.5 w-3.5 text-muted-foreground" />{lead.primaryName}</div>
                    <div className="flex items-center gap-2 text-sm"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{lead.primaryPhone}</div>
                    <div className="flex items-center gap-2 text-sm"><Mail className="h-3.5 w-3.5 text-muted-foreground" />{lead.primaryEmail}</div>
                  </div>
                  {lead.secondaryName && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">Secondary Contact</p>
                      <div className="flex items-center gap-2 text-sm"><User className="h-3.5 w-3.5 text-muted-foreground" />{lead.secondaryName}</div>
                      {lead.secondaryPhone && <div className="flex items-center gap-2 text-sm"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{lead.secondaryPhone}</div>}
                      {lead.secondaryEmail && <div className="flex items-center gap-2 text-sm"><Mail className="h-3.5 w-3.5 text-muted-foreground" />{lead.secondaryEmail}</div>}
                    </div>
                  )}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Property</p>
                    <div className="flex items-center gap-2 text-sm"><MapPin className="h-3.5 w-3.5 text-muted-foreground" />{lead.address}, {lead.city}, {lead.state} {lead.zip}</div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Notes</p>
                    <p className="text-sm text-muted-foreground">{lead.notes || "—"}</p>
                  </div>
                </div>
              ) : editForm && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Primary Name</Label><Input value={editForm.primaryName} onChange={(e) => setEditForm({ ...editForm, primaryName: e.target.value })} className="h-8" /></div>
                  <div><Label className="text-xs">Secondary Name</Label><Input value={editForm.secondaryName} onChange={(e) => setEditForm({ ...editForm, secondaryName: e.target.value })} className="h-8" /></div>
                  <div><Label className="text-xs">Primary Phone</Label><Input value={editForm.primaryPhone} onChange={(e) => setEditForm({ ...editForm, primaryPhone: e.target.value })} className="h-8" /></div>
                  <div><Label className="text-xs">Primary Email</Label><Input value={editForm.primaryEmail} onChange={(e) => setEditForm({ ...editForm, primaryEmail: e.target.value })} className="h-8" /></div>
                  <div><Label className="text-xs">Secondary Phone</Label><Input value={editForm.secondaryPhone} onChange={(e) => setEditForm({ ...editForm, secondaryPhone: e.target.value })} className="h-8" /></div>
                  <div><Label className="text-xs">Secondary Email</Label><Input value={editForm.secondaryEmail} onChange={(e) => setEditForm({ ...editForm, secondaryEmail: e.target.value })} className="h-8" /></div>
                  <div className="col-span-2"><Label className="text-xs">Address</Label><Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="h-8" /></div>
                  <div><Label className="text-xs">City</Label><Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} className="h-8" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">State</Label><Input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} className="h-8" /></div>
                    <div><Label className="text-xs">ZIP</Label><Input value={editForm.zip} onChange={(e) => setEditForm({ ...editForm, zip: e.target.value })} className="h-8" /></div>
                  </div>
                  <div className="col-span-2"><Label className="text-xs">Notes</Label><Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={2} /></div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Timeline */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Activity</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setShowActivity(true)}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Log Activity
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[...lead.activities].reverse().map((act) => {
                  const Icon = activityIcons[act.type] || Clock;
                  return (
                    <div key={act.id} className="flex gap-3">
                      <div className="mt-0.5">
                        <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground">{act.description}</p>
                        <p className="text-xs text-muted-foreground">{formatSafe(act.createdAt, "MMM d, h:mm a")} · {getRepDisplayName(act.createdBy)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Messages */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4" /> Messages
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {conversationId && (
                <ConversationThread
                  conversationId={conversationId}
                  contactName={lead.primaryName}
                  contactPhone={lead.primaryPhone}
                  compact
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-4">
          {/* Attribution */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Megaphone className="h-4 w-4" /> Attribution
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Lead Source</span>
                <Badge variant="outline" className="text-xs">{lead.leadSource}</Badge>
              </div>
              {(() => {
                const channelLabel = getGrowthChannelLabel(lead.leadSource);
                const channelId = suggestChannel(lead.leadSource);
                const channel = channelId ? getChannelById(channelId) : null;
                const displayLabel = channelLabel || (channel ? channel.name : null);
                if (!displayLabel) return (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Channel</span>
                    <span className="text-xs text-muted-foreground/60">Unmapped</span>
                  </div>
                );
                return (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Channel</span>
                    <div className="flex items-center gap-1">
                      <Link2 className="h-3 w-3 text-primary" />
                      <span className="text-xs font-medium text-primary">{displayLabel}</span>
                    </div>
                  </div>
                );
              })()}
              {/* Mini attribution flow */}
              <div className="pt-2 border-t">
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  {(() => {
                    const channelId = suggestChannel(lead.leadSource);
                    const channel = channelId ? getChannelById(channelId) : null;
                    return (
                      <>
                        {channel && <span className="font-medium text-primary">{channel.name}</span>}
                        {channel && <ChevronRight className="h-2.5 w-2.5" />}
                        <span className="font-medium">{lead.leadSource}</span>
                        <ChevronRight className="h-2.5 w-2.5" />
                        <span>Lead</span>
                        {quoteIdForLink && <><ChevronRight className="h-2.5 w-2.5" /><span>Quote</span></>}
                        {lead.linkedJobId && <><ChevronRight className="h-2.5 w-2.5" /><span className="font-medium text-foreground">Job</span></>}
                      </>
                    );
                  })()}
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Appointment Status */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="h-4 w-4" /> Appointment
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lead.appointmentTime ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={new Date(lead.appointmentTime) > new Date() ? "default" : "secondary"} className="text-xs">
                      {new Date(lead.appointmentTime) > new Date() ? "Upcoming" : "Completed"}
                    </Badge>
                  </div>
                  <p className="text-sm font-medium">
                    {new Date(lead.appointmentTime).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </p>
                  <p className="text-xs text-muted-foreground">{safeRepLabel(lead.assignedRep)}</p>
                </div>
              ) : (
                <div className="text-center py-2">
                  <p className="text-xs text-muted-foreground mb-2">No appointment scheduled</p>
                  {lead.stage !== "Closed Lost" && lead.stage !== "Closed Won" && (
                    <Button size="sm" variant="outline" className="w-full" onClick={() => {
                      const candidateAssigned = String(lead.assignedRep ?? "").trim();
                      const defaultRep = isUuid(candidateAssigned)
                        ? candidateAssigned
                        : (currentRepId && isUuid(currentRepId) ? currentRepId : "");
                      setSchedDate(format(new Date(Date.now() + 86400000), "yyyy-MM-dd"));
                      setScheduleRep(defaultRep);
                      setShowSchedule(true);
                    }}>
                      <CalendarPlus className="h-3.5 w-3.5 mr-1" /> Schedule Now
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Follow-Up Status */}
          {(lead.followUpType || lead.followUpDueDate || lead.dispositionReason) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Follow-Up</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {lead.followUpType && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Type</span>
                    <Badge variant="outline" className="text-xs capitalize">{lead.followUpType}</Badge>
                  </div>
                )}
                {lead.followUpDueDate && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Due</span>
                    <span className={cn("text-xs font-medium", new Date(lead.followUpDueDate) < new Date() ? "text-destructive" : "text-foreground")}>
                      {new Date(lead.followUpDueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  </div>
                )}
                {lead.dispositionReason && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Reason</span>
                    <span className="text-xs text-foreground capitalize">{lead.dispositionReason.replace(/_/g, " ")}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Last Contact */}
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Last Contact</p>
              {leadConversation ? (
                <div>
                  <p className="text-sm font-medium">{leadConversation.lastMessagePreview || "No messages yet"}</p>
                  <p className="text-xs text-muted-foreground">{formatSafe(leadConversation.updatedAt, "MMM d, h:mm a")}</p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No contact history</p>
              )}
            </CardContent>
          </Card>
          {/* Ownership */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Ownership</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{safeRepLabel(lead.assignedRep)}</span>
                </div>
                {role !== "sales" && (
                  <Button size="sm" variant="ghost" onClick={() => { setNewRep(lead.assignedRep); setShowReassign(true); }}>
                    Reassign
                  </Button>
                )}
              </div>
              {lead.ownershipHistory.length > 1 && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground mb-1">History</p>
                  {lead.ownershipHistory.map((oh) => (
                    <p key={oh.id} className="text-xs text-muted-foreground">
                      {oh.fromRep ? `${oh.fromRep} → ${oh.toRep}` : `Assigned to ${oh.toRep}`} · {oh.changedAt}
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Linked Records */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Linked Records</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {quoteIdForLink ? (
                <Link to={`/opportunities/${quoteIdForLink}`} className="flex items-center justify-between p-2 rounded-md bg-muted/50 hover:bg-muted transition-colors">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{linkedQuote?.displayReference ?? quoteIdForLink}</p>
                      {linkedQuote && <p className="text-xs text-muted-foreground">{linkedQuote.status} · ${linkedQuote.totalEstimate?.toLocaleString()}</p>}
                    </div>
                    
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ) : (
                <p className="text-xs text-muted-foreground">No linked quote</p>
              )}
              {lead.linkedJobId ? (
                <Link to={`/jobs/${lead.linkedJobId}`} className="flex items-center justify-between p-2 rounded-md bg-muted/50 hover:bg-muted transition-colors">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-medium">{lead.linkedJobId}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ) : (
                <p className="text-xs text-muted-foreground">No linked job</p>
              )}
            </CardContent>
          </Card>

          {/* Stage History */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Stage History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {[...lead.stageHistory].reverse().map((sh) => (
                  <div key={sh.id} className="text-xs">
                    <span className="text-muted-foreground">{formatSafe(sh.changedAt, "MMM d, h:mm a")}</span>
                    <span className="mx-1">·</span>
                    {sh.fromStage ? (
                      <span>{sh.fromStage} → <span className="font-medium text-foreground">{sh.toStage}</span></span>
                    ) : (
                      <span className="font-medium text-foreground">{sh.toStage}</span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Meta */}
          <Card>
            <CardContent className="p-4 space-y-1 text-xs text-muted-foreground">
              <p>Created: {formatSafe(lead.createdAt, "MMM d, yyyy, h:mm a")}</p>
              <p>Updated: {formatSafe(lead.updatedAt, "MMM d, yyyy, h:mm a")}</p>
              <p>Source: {lead.leadSource}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Activity Dialog */}
      <Dialog open={showActivity} onOpenChange={setShowActivity}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={activityType} onValueChange={(v: any) => setActivityType(v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Call Made</SelectItem>
                  <SelectItem value="note">Note Added</SelectItem>
                  <SelectItem value="follow_up">Follow-up Scheduled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={activityText} onChange={(e) => setActivityText(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivity(false)}>Cancel</Button>
            <PermissionGate permission="leads_edit">
              <Button onClick={handleAddActivity}>Save</Button>
            </PermissionGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={showReassign} onOpenChange={setShowReassign}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reassign Lead</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs">New Sales Rep</Label>
            <Select value={newRep} onValueChange={setNewRep}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {reps.map((r) => <SelectItem key={r} value={r}>{safeRepLabel(r)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassign(false)}>Cancel</Button>
            <PermissionGate permission="leads_edit">
              <Button onClick={handleReassign}>Reassign</Button>
            </PermissionGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>

       {/* Schedule Appointment Dialog */}
       <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
         <DialogContent className="max-w-md">
           <DialogHeader><DialogTitle>Schedule Appointment</DialogTitle></DialogHeader>
           <div className="space-y-4">
             <div className="space-y-2">
               <Label className="text-xs">Assigned Rep</Label>
               <Select value={scheduleRep} onValueChange={setScheduleRep}>
                 <SelectTrigger className="h-9 w-full"><SelectValue /></SelectTrigger>
                 <SelectContent>
                   {reps.map((r) => <SelectItem key={r} value={r}>{getRepDisplayName(r)}</SelectItem>)}
                 </SelectContent>
               </Select>
             </div>
             <div className="grid grid-cols-2 gap-3">
               <div>
                 <Label className="text-xs">Date</Label>
                 <Input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} className="h-9" />
               </div>
               <div>
                 <Label className="text-xs">Time</Label>
                 <Input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} className="h-9" />
               </div>
             </div>

             {/* Booking Intelligence */}
             {(() => {
               const leadZip = lead?.address?.split(",").pop()?.trim().split(" ").pop() || "33606";
               const suggestions = getBookingSuggestions(leadZip, undefined, 70);
               if (suggestions.length === 0) return null;
               return (
                 <div className="space-y-2">
                   <div className="flex items-center gap-1.5">
                     <Zap className="h-3 w-3 text-primary" />
                     <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Suggested Options</p>
                   </div>
                   <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                     {suggestions.slice(0, 4).map((s, i) => (
                       <div
                         key={i}
                         className={cn(
                           "rounded-lg border p-2 cursor-pointer transition-all hover:border-primary/30",
                           i === 0 ? "border-primary/25 bg-primary/5" : "bg-muted/20",
                         )}
                         onClick={() => setSchedTime(s.timeWindow.includes("AM") ? "10:00" : "14:00")}
                       >
                         <div className="flex items-center justify-between">
                           <div className="flex items-center gap-1.5">
                             {i === 0 && <Target className="h-3 w-3 text-primary" />}
                             <span className="text-[11px] font-semibold">{safeRepLabel(s.rep)}</span>
                           </div>
                           <div className="flex items-center gap-1.5">
                             <span className={cn("text-[12px] font-bold", i === 0 ? "text-success" : s.fitLabel === "Good Option" ? "text-info" : "text-muted-foreground")}>
                               {s.fitScore}
                             </span>
                             <Badge variant="outline" className={`text-[8px] px-1 py-0 ${s.fitBadgeClass}`}>{s.fitLabel}</Badge>
                           </div>
                         </div>
                         <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                           <span>{s.timeWindow}</span>
                           <span>·</span>
                           <span>{s.proximityNote}</span>
                         </div>
                         <p className="text-[9px] text-muted-foreground/70 italic mt-0.5">{s.microcopy}</p>
                       </div>
                     ))}
                   </div>
                 </div>
               );
             })()}
             {scheduleConflictLead && (
               <div className="rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-xs text-warning">
                 Conflict: {scheduleRep || "Selected rep"} is already booked for another appointment within the 2-hour appointment + 2-hour buffer window.
               </div>
             )}
           </div>
<DialogFooter>
  <Button variant="outline" onClick={() => setShowSchedule(false)}>Cancel</Button>
  <PermissionGate permission="leads_edit">
    <Button
      onClick={async () => {
        setIsScheduling(true);
        try {
          await handleScheduleAppointment();
        } finally {
          setIsScheduling(false);
        }
      }}
      disabled={!schedDate || !!scheduleConflictLead || !isUuid(scheduleRep) || isScheduling}
    >
      {isScheduling ? "Scheduling..." : "Schedule"}
    </Button>
  </PermissionGate>
</DialogFooter>
         </DialogContent>
       </Dialog>
    </div>
  );
}
